<template>
  <div class="card">
    <h2>{{ nombreDifunto }}</h2>
    <div class="links">
      <section class="link">
        <qrcode-vue
          :value="obertQrVal"
          :level="qrLevel"
          :render-as="qrRenderAs"
          :class="obertQrClassName"
          :size="200"
        />
        <h3>QR OBERT</h3>
        <p class="qr-desc qr-desc--obert">
          <font-awesome-icon icon="eye" />
          Contingut visible immediatament
        </p>
        <div class="buttons">
          <button class="link-button" @click="goLink(obertQrVal)">
            <font-awesome-icon icon="file-arrow-up" />
            Ves a l'enllaç
          </button>
          <button class="link-button" @click="copyClipboard(obertQrVal)">
            <font-awesome-icon icon="copy" />
            Copia l'enllaç
          </button>
          <button
            class="link-button"
            @click="downloadQr(obertQrClassName, nombreDifunto)"
          >
            <font-awesome-icon icon="floppy-disk" />
            Descarrega QR
          </button>
        </div>
      </section>
      <span></span>
      <div class="separator"></div>
      <span></span>
      <section class="link">
        <qrcode-vue
          :value="tancatQrVal"
          :level="qrLevel"
          :render-as="qrRenderAs"
          :class="tancatQrClassName"
          :size="200"
        />
        <h3>QR TANCAT</h3>
        <p class="qr-desc qr-desc--tancat">
          <font-awesome-icon icon="eye-slash" />
          Cal validació de la família
        </p>
        <div class="buttons">
          <button class="link-button" @click="goLink(tancatQrVal)">
            <font-awesome-icon icon="file-arrow-up" />
            Ves a l'enllaç
          </button>
          <button class="link-button" @click="copyClipboard(tancatQrVal)">
            <font-awesome-icon icon="copy" />
            Copia l'enllaç
          </button>
          <button
            class="link-button"
            @click="downloadQr(tancatQrClassName, nombreDifunto)"
          >
            <font-awesome-icon icon="floppy-disk" />
            Descarrega QR
          </button>
        </div>
      </section>
      <section class="link">
        <div class="filtre-icon">
          <font-awesome-icon icon="pen" class="icon" />
        </div>
        <h3>FILTRE MISSATGES</h3>
        <button class="link-button" @click="goLink(editaPageUrl)">
          <font-awesome-icon icon="photo-film" />
          Ves a l'enllaç
        </button>
        <button class="link-button" @click="copyClipboard(editaPageUrl)">
          <font-awesome-icon icon="copy" />
          Copia l'enllaç
        </button>
      </section>
    </div>
    <div></div>
    <div></div>
    <div class="envia-flia">
      <span>Seleccionat: {{ textTipoQr }}</span>
      <input
        class="form-input"
        type="text"
        placeholder="CORREU CONTACTE"
        v-model="correoContacto"
        disabled
      />
      <button class="envia-button" @click="sendMailQr">
        <font-awesome-icon icon="paper-plane" />
        ENVIA QR
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from "vue";

import axios from "axios";
import QrcodeVue from "qrcode.vue";

import useErpApiClient from "../erp-api-client";
import useQr from "../qr";

import { Vetlla } from "../types/vetlla.ts";
import CustomSwal from "../custom-swal.ts";

export default defineComponent({
  components: {
    QrcodeVue,
  },
  props: {
    idVetlla: {
      required: true,
      type: Number,
    },
  },
  setup(props) {
    const erpClient = useErpApiClient();

    const nombreDifunto = ref<string>("Indefinit");
    const textTipoQr = ref<string>("-");
    const correoContacto = ref<string>("");
    const vetlla = ref<null | Vetlla>(null);

    const baseUrl = import.meta.env.VITE_HOST;

    const {
      obertQrVal,
      tancatQrVal,
      qrLevel,
      qrRenderAs,
      obertQrClassName,
      tancatQrClassName,
      setQrVals,
      downloadQr,
    } = useQr(baseUrl);

    const editaPageUrl = ref("");

    onMounted(() => {
      axios.get(`/api/vetlla/read?id=${props.idVetlla}`).then((response) => {
        vetlla.value = response.data as Vetlla;
        nombreDifunto.value = vetlla.value.NombreDifunto;
        textTipoQr.value = vetlla.value.IsQrObert ? "OBERT" : "TANCAT";
        correoContacto.value = vetlla.value.CorreoContacto;

        setQrVals(vetlla.value.Id);

        editaPageUrl.value = baseUrl + "/diapositivas/" + vetlla.value.Id;
      });
    });

    const copyClipboard = (qrVal: string) => {
      navigator.clipboard.writeText(qrVal);
    };

    const sendMailQr = () => {
      CustomSwal.fire({
        title: "Envia correu a la família",
        icon: "question",
        html: `Es tornarà a enviar el correu informatiu sobre la presentació de la vetlla i els enllaços de QR/filtratge de contingut. Enviar el QR <b>${textTipoQr.value}</b> a l'adreça '<b>${correoContacto.value}</b>'.`,
        showCancelButton: true,
        focusConfirm: false,
        confirmButtonText: "Confirma",
        cancelButtonText: "Cancel·la",
      }).then((result) => {
        if (result.isConfirmed) {
          if (null !== vetlla.value) {
            erpClient
              .request(() =>
                axios.post("/api/mail/send-qr", {
                  IdVetlla: vetlla.value?.Id,
                }),
              )
              .then(() => {
                CustomSwal.fire({
                  title: "Èxit",
                  icon: "success",
                });
              })
              .catch((e) => {
                console.error(e);
                CustomSwal.fire({
                  title: "Error",
                  text: "No s'ha pogut enviar el correu",
                  icon: "error",
                });
              });
          }
        }
      });
    };

    const goLink = (url: string) => {
      window.open(url, "_blank");
    };

    return {
      nombreDifunto,
      textTipoQr,
      correoContacto,
      obertQrVal,
      tancatQrVal,
      qrLevel,
      qrRenderAs,
      obertQrClassName,
      tancatQrClassName,
      copyClipboard,
      downloadQr,
      editaPageUrl,
      sendMailQr,
      goLink,
    };
  },
});
</script>

<style lang="scss" scoped>
.card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-evenly;
  gap: 1em;

  padding: 1em;
  border-radius: $rounded-border;

  background-color: $bg-color;

  width: 80%;
}

.links {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 2em;
}

.link {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4em;
}

.buttons {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2em;
}

.link-button {
  @include button($accessory-color);
}

.envia-flia {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1em;
}

.envia-button {
  @include button($primary-color);
}

.filtre-icon {
  // mismo size que Qr
  height: 200px;
  > .icon {
    font-size: 4em;
    padding: 1em;
    background-color: $primary-bg-color;
    border-radius: 50%;
  }
}

.separator {
  border: 1px solid #444;
  align-self: stretch;
}

.qr-desc {
  display: flex;
  align-items: center;
  gap: 0.4em;
  font-size: 0.85em;
  color: $text-color;
  margin: 0.2em 0 0.6em 0;

  &--obert {
    color: #48bb78;
  }

  &--tancat {
    color: #e53e3e;
  }
}
</style>
